import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import ScrollReveal from "@/components/base/ScrollReveal";
import { usePageContent } from "@/hooks/usePageContent";

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

const cases = [
  {
    tag: "Hospital",
    tagColor: "bg-primary-500/10 text-primary-700",
    title: "Undeveloped Plot to Multi-Specialty Healthcare Hub",
    location: "Mysuru, Karnataka",
    plotSize: "3 Acres",
    strategy: "Healthcare Campus Development",
    timeline: "24 months",
    desc: "A 3-acre undeveloped plot near the Bengaluru-Mysuru highway sat idle for over a decade despite being surrounded by a rapidly growing residential catchment. Our AI catchment analysis identified a severe doctor-density deficit across five neighboring districts. The land was developed into a 150-bed multi-specialty hospital with dedicated oncology, cardiology, and maternity wings.",
    before: ["Idle highway-adjacent plot", "Zero community infrastructure", "No medical facilities within 25 km", "Overgrown with wild vegetation"],
    afterTitle: "After 24 Months",
    afterMetrics: [
      { label: "Bed Capacity", value: "150 Beds" },
      { label: "Specialty Wings", value: "8 Departments" },
      { label: "Catchment Coverage", value: "5 Districts" },
      { label: "Daily Outpatients", value: "350+" },
    ],
    testimonial: "What was an overgrown wasteland is now the only advanced healthcare facility within 30 kilometers. Patients travel from five districts to reach us.",
    person: "Dr. Lakshmi N., Hospital Director, Mysuru",
    image: "https://readdy.ai/api/search-image?query=Modern%20multi-specialty%20hospital%20building%20in%20India%20at%20golden%20hour%2C%20clean%20white%20facade%20with%20glass%20accents%2C%20landscaped%20entrance%20with%20palm%20trees%2C%20ambulance%20and%20patients%20arriving%2C%20warm%20natural%20lighting%2C%20editorial%20architectural%20photography%2C%20neutral%20sky%20background&width=800&height=600&seq=case-hospital-01&orientation=landscape",
  },
  {
    tag: "School",
    tagColor: "bg-secondary-500/10 text-secondary-700",
    title: "Barren Land to Premier Educational Campus",
    location: "Whitefield, Bengaluru",
    plotSize: "5 Acres",
    strategy: "CBSE-Affiliated School Campus",
    timeline: "18 months",
    desc: "5 acres of rocky, barren land on the outskirts of Bengaluru had no access road and zero development interest. Our enrolment-demand model revealed a projected shortfall of 4,000 school seats in the surrounding 8-kilometer radius within five years. The site was transformed into a full-service CBSE campus with science labs, sports grounds, and a 500-seat auditorium.",
    before: ["Rocky barren terrain", "No access road", "Zero development interest", "No schools within 5 km radius"],
    afterTitle: "After 18 Months",
    afterMetrics: [
      { label: "Student Capacity", value: "2,000" },
      { label: "Grade Levels", value: "LKG to XII" },
      { label: "Sports Facilities", value: "5 Acres" },
      { label: "Admission Rate", value: "98%" },
    ],
    testimonial: "Enrollment opened in January and we had a waitlist by March. The AI demand prediction was spot on — parents had been driving 12 kilometers to the nearest school.",
    person: "Ravi K., Principal, Bengaluru",
    image: "https://readdy.ai/api/search-image?query=Modern%20CBSE%20school%20campus%20in%20India%20with%20open%20courtyards%20and%20green%20lawns%2C%20children%20in%20uniform%20playing%20on%20sports%20ground%2C%20contemporary%20architecture%20with%20red%20brick%20and%20white%20walls%2C%20bright%20sunny%20day%2C%20editorial%20educational%20photography%2C%20clean%20neutral%20background%20sky&width=800&height=600&seq=case-school-02&orientation=landscape",
  },
  {
    tag: "Hotel",
    tagColor: "bg-primary-500/10 text-primary-700",
    title: "Scenic Hillside to Boutique Resort",
    location: "Coorg, Karnataka",
    plotSize: "4 Acres",
    strategy: "Boutique Hospitality Development",
    timeline: "14 months",
    desc: "A 4-acre hillside plot in Coorg with stunning valley views had no road access and had been passed over by developers for years. Our ADR and occupancy model identified a strong demand for experiential stays among Bengaluru weekend travelers. A 40-room boutique resort was built with infinity pools, a spa, and curated coffee estate experiences.",
    before: ["Hillside with no road access", "Overgrown coffee plants", "No utility connections", "Ignored by developers for years"],
    afterTitle: "After 14 Months",
    afterMetrics: [
      { label: "Room Count", value: "40 Rooms" },
      { label: "Year-Round Occupancy", value: "85%" },
      { label: "Weekend Waitlist", value: "6 Weeks" },
      { label: "Guest Rating", value: "4.7 / 5" },
    ],
    testimonial: "Before this, travelers to Coorg had two choices: generic hotels or homestays. We created a third category entirely, and the weekend waitlist proves it.",
    person: "Meera D., Resort Owner, Coorg",
    image: "https://readdy.ai/api/search-image?query=Boutique%20hillside%20resort%20in%20Coorg%20Karnataka%20India%20at%20twilight%2C%20infinity%20pool%20overlooking%20misty%20valley%2C%20modern%20wooden%20villas%20among%20coffee%20plants%2C%20warm%20interior%20lighting%20glowing%20from%20windows%2C%20serene%20atmospheric%20photography%2C%20neutral%20earth%20tones&width=800&height=600&seq=case-hotel-03&orientation=landscape",
  },
  {
    tag: "Warehouse",
    tagColor: "bg-secondary-500/10 text-secondary-700",
    title: "Agricultural Land to Logistics Hub",
    location: "Hosur, Tamil Nadu",
    plotSize: "8 Acres",
    strategy: "3PL Distribution Center",
    timeline: "12 months",
    desc: "8 acres of underutilized agricultural land near the Bengaluru-Chennai highway corridor produced marginal seasonal crops. Our freight-corridor analysis flagged the location as ideal for a mid-mile distribution hub serving South India. A 150,000 sq.ft Grade-A warehouse with cold-chain capability and automated sorting was constructed.",
    before: ["Marginal seasonal crop land", "No warehousing within 20 km", "Poor soil quality", "Seasonal income only"],
    afterTitle: "After 12 Months",
    afterMetrics: [
      { label: "Warehouse Area", value: "150,000 sq.ft" },
      { label: "Cold-Chain Zones", value: "4 Temperatures" },
      { label: "Daily Dispatch Volume", value: "12,000 Units" },
      { label: "3PL Partners", value: "6 Brands" },
    ],
    testimonial: "The highway made it invisible as farmland but perfect as a logistics node. Now six major brands move inventory through our facility daily.",
    person: "Vikram S., Logistics Partner, Hosur",
    image: "https://readdy.ai/api/search-image?query=Modern%20logistics%20warehouse%20distribution%20center%20in%20India%20with%20trucks%20loading%20at%20docks%2C%20large%20industrial%20building%20with%20steel%20cladding%2C%20organized%20pallet%20racking%20visible%20inside%2C%20blue%20sky%20with%20few%20clouds%2C%20editorial%20industrial%20photography%2C%20clean%20professional%20look&width=800&height=600&seq=case-warehouse-04&orientation=landscape",
  },
  {
    tag: "Retail",
    tagColor: "bg-primary-500/10 text-primary-700",
    title: "Idle Corner to Vibrant Shopping Destination",
    location: "Whitefield, Bengaluru",
    plotSize: "2 Acres",
    strategy: "Neighborhood Retail Center",
    timeline: "10 months",
    desc: "A 2-acre corner plot at a high-visibility Whitefield intersection sat fenced and empty for 6 years, gradually becoming a dumping ground. Our footfall and trade-area analysis identified 18,000 daily passersby and a retail spend gap of over 40% versus comparable neighborhoods. A compact neighborhood center with 12 curated brands was developed.",
    before: ["Fenced empty corner plot", "Illegal dumping ground", "Zero pedestrian engagement", "Eyesore for 6 years"],
    afterTitle: "After 10 Months",
    afterMetrics: [
      { label: "Brand Tenants", value: "12 Brands" },
      { label: "Daily Footfall", value: "4,500" },
      { label: "Anchor Occupancy", value: "100%" },
      { label: "Repeat Visitor Rate", value: "62%" },
    ],
    testimonial: "For six years it was an embarrassment. Now it is the neighborhood anchor — families meet here, kids play in the courtyard, and every brand wants in.",
    person: "Sunita R., Property Owner, Whitefield",
    image: "https://readdy.ai/api/search-image?query=Modern%20neighborhood%20retail%20shopping%20center%20in%20India%20at%20evening%2C%20open%20courtyard%20with%20families%20walking%2C%20curated%20brand%20storefronts%20with%20warm%20lighting%2C%20clean%20contemporary%20architecture%2C%20lively%20community%20atmosphere%2C%20editorial%20commercial%20photography%2C%20neutral%20sky%20background&width=800&height=600&seq=case-retail-05&orientation=landscape",
  },
  {
    tag: "Office",
    tagColor: "bg-secondary-500/10 text-secondary-700",
    title: "Dormant Land to Grade-A IT Park",
    location: "Gachibowli, Hyderabad",
    plotSize: "6 Acres",
    strategy: "LEED-Certified Office Complex",
    timeline: "30 months",
    desc: "6 acres of dormant government-allotted land in Hyderabad's Gachibowli tech corridor had remained undeveloped for 8 years due to fragmented ownership. Our corridor-absorption model predicted a 200,000 sq.ft shortfall of Grade-A office stock within 3 years. A unified ownership structure and a 500,000 sq.ft LEED-certified office campus was developed.",
    before: ["Fragmented ownership", "8 years undeveloped", "No utility infrastructure", "Overgrown with shrubs"],
    afterTitle: "After 30 Months",
    afterMetrics: [
      { label: "Office Space", value: "500,000 sq.ft" },
      { label: "LEED Certification", value: "Gold" },
      { label: "Tenant Occupancy", value: "92%" },
      { label: "Workforce Capacity", value: "4,500" },
    ],
    testimonial: "Eight years of deadlock ended when the data made the opportunity undeniable. Today this campus houses three Fortune 500 back-offices.",
    person: "Arjun M., Development Partner, Hyderabad",
    image: "https://readdy.ai/api/search-image?query=Modern%20Grade-A%20IT%20office%20park%20in%20Hyderabad%20India%2C%20glass%20and%20steel%20high-rise%20buildings%20with%20landscaped%20gardens%2C%20professionals%20walking%20on%20pathways%2C%20clean%20contemporary%20architecture%2C%20bright%20daylight%2C%20editorial%20corporate%20real%20estate%20photography%2C%20blue%20sky%20with%20light%20clouds&width=800&height=600&seq=case-office-06&orientation=landscape",
  },
  {
    tag: "Data Center",
    tagColor: "bg-primary-500/10 text-primary-700",
    title: "Remote Land to Hyperscale Facility",
    location: "Vizag, Andhra Pradesh",
    plotSize: "10 Acres",
    strategy: "Hyperscale Data Center",
    timeline: "22 months",
    desc: "10 acres of remote land near Visakhapatnam had reliable grid power but was dismissed by developers due to its distance from major metros. Our latency and fiber-analysis model identified two submarine cable landing stations within 50 kilometers, making it ideal for a disaster-recovery data center. A 20MW Tier-III facility was constructed with direct fiber links.",
    before: ["Remote land with no demand", "Reliable power but no takers", "50 km from metro", "Overlooked by developers"],
    afterTitle: "After 22 Months",
    afterMetrics: [
      { label: "Power Capacity", value: "20 MW" },
      { label: "Tier Rating", value: "Tier-III" },
      { label: "Fiber Links", value: "2 Submarine" },
      { label: "Uptime SLA", value: "99.982%" },
    ],
    testimonial: "Everyone saw the distance. We saw the fiber. Today this facility serves as primary infrastructure for three cloud providers.",
    person: "Naveen P., Infrastructure Lead, Vizag",
    image: "https://readdy.ai/api/search-image?query=Modern%20hyperscale%20data%20center%20building%20exterior%20in%20India%2C%20clean%20white%20industrial%20facade%20with%20cooling%20towers%2C%20secure%20perimeter%20fencing%2C%20dramatic%20twilight%20sky%2C%20editorial%20technology%20infrastructure%20photography%2C%20minimal%20and%20professional%20aesthetic&width=800&height=600&seq=case-datacenter-07&orientation=landscape",
  },
  {
    tag: "EV Charging",
    tagColor: "bg-secondary-500/10 text-secondary-700",
    title: "Highway Land to EV Charging Corridor",
    location: "NH-48, Mumbai-Pune Expressway",
    plotSize: "1.5 Acres",
    strategy: "Fast-Charging Infrastructure Hub",
    timeline: "6 months",
    desc: "A 1.5-acre plot at a critical Mumbai-Pune Expressway intersection had been used as an informal truck stop with zero structured development. Our traffic-volume and dwell-time model showed 35,000 vehicles passing daily with an average dwell of 22 minutes — the perfect profile for EV fast-charging. A 50-point fast-charging hub with amenities was installed.",
    before: ["Informal truck stop", "No structured development", "High traffic but zero amenities", "Dusty and unmanaged"],
    afterTitle: "After 6 Months",
    afterMetrics: [
      { label: "Charging Points", value: "50" },
      { label: "Daily Sessions", value: "800+" },
      { label: "Average Dwell", value: "28 min" },
      { label: "Grid Capacity", value: "10 MW" },
    ],
    testimonial: "Drivers used to stop here because they had no choice. Now they plan their route specifically to charge here — the amenities made it a destination.",
    person: "Rohan T., EV Infrastructure Partner, Pune",
    image: "https://readdy.ai/api/search-image?query=Modern%20EV%20fast%20charging%20station%20on%20Indian%20highway%20at%20dusk%2C%20multiple%20electric%20cars%20charging%20under%20sleek%20canopy%20with%20LED%20lighting%2C%20clean%20contemporary%20design%2C%20dramatic%20sky%2C%20editorial%20infrastructure%20photography%2C%20professional%20and%20futuristic%20aesthetic&width=800&height=600&seq=case-ev-08&orientation=landscape",
  },
  {
    tag: "Parking",
    tagColor: "bg-primary-500/10 text-primary-700",
    title: "Underutilized Plot to Smart Parking Tower",
    location: "Camp, Pune",
    plotSize: "1 Acre",
    strategy: "Automated Multi-Level Parking",
    timeline: "14 months",
    desc: "A 1-acre plot in central Pune's Camp area was used for surface-level parking of just 35 vehicles despite being surrounded by offices, hospitals, and retail. Our demand-deficit model revealed a parking shortfall of 800+ bays within a 500-meter radius. A 6-level automated parking tower was constructed, multiplying capacity by over 10x.",
    before: ["Surface parking for 35 cars", "Surrounded by offices and hospitals", "No vertical development", "Chronic parking shortage nearby"],
    afterTitle: "After 14 Months",
    afterMetrics: [
      { label: "Vehicle Capacity", value: "400 Cars" },
      { label: "Levels", value: "6 Floors" },
      { label: "Average Retrieval", value: "90 sec" },
      { label: "Utilization Rate", value: "94%" },
    ],
    testimonial: "We went from 35 cars on dirt to 400 cars in a fully automated tower. The retrieval time is under 90 seconds and it is full by 9 AM every day.",
    person: "Anil K., Parking Operator, Pune",
    image: "https://readdy.ai/api/search-image?query=Modern%20automated%20multi-level%20car%20parking%20tower%20in%20India%2C%20sleek%20steel%20and%20glass%20structure%20with%20cars%20visible%20on%20rotating%20platforms%2C%20urban%20setting%20with%20office%20buildings%20around%2C%20clean%20daylight%20photography%2C%20editorial%20architectural%20infrastructure%2C%20neutral%20city%20background&width=800&height=600&seq=case-parking-09&orientation=landscape",
  },
  {
    tag: "Cloud Kitchen",
    tagColor: "bg-secondary-500/10 text-secondary-700",
    title: "Empty Warehouse to Food Delivery Hub",
    location: "Gurgaon, Haryana",
    plotSize: "8,000 sq.ft",
    strategy: "Multi-Brand Cloud Kitchen",
    timeline: "4 months",
    desc: "An 8,000 sq.ft disused industrial shed in Gurgaon's Sector 62 had broken roofing and zero tenants. Our delivery-density analysis revealed 12,000 daily food orders within a 3-kilometer radius but only two operational kitchens. The shed was converted into a 15-brand cloud kitchen with dedicated prep zones, cold storage, and dispatch bays.",
    before: ["Disused industrial shed", "Broken roofing and no tenants", "High delivery demand nearby", "Zero food infrastructure"],
    afterTitle: "After 4 Months",
    afterMetrics: [
      { label: "Brand Kitchens", value: "15" },
      { label: "Daily Orders", value: "3,500" },
      { label: "Prep Zones", value: "12 Dedicated" },
      { label: "Dispatch Speed", value: "8 min avg" },
    ],
    testimonial: "Four months from a leaky shed to fifteen brands operating under one roof. The delivery riders line up at 11 AM sharp every single day.",
    person: "Neha B., Kitchen Operations Head, Gurgaon",
    image: "https://readdy.ai/api/search-image?query=Modern%20cloud%20kitchen%20food%20delivery%20facility%20interior%20in%20India%2C%20multiple%20professional%20cooking%20stations%20with%20stainless%20steel%20equipment%2C%20organized%20prep%20areas%2C%20delivery%20bags%20ready%20for%20dispatch%2C%20bright%20clean%20lighting%2C%20editorial%20commercial%20food%20photography%2C%20professional%20industrial%20aesthetic&width=800&height=600&seq=case-cloudkitchen-10&orientation=landscape",
  },
  {
    tag: "Co-living",
    tagColor: "bg-primary-500/10 text-primary-700",
    title: "Old Residential Block to Modern Co-living Space",
    location: "Kondapur, Hyderabad",
    plotSize: "3 Acres",
    strategy: "Professional Co-living Community",
    timeline: "16 months",
    desc: "3 acres with crumbling residential structures in Hyderabad's Kondapur tech hub housed just 12 families in unsafe conditions. Our workforce-demand model identified 8,000 young professionals within 3 kilometers seeking affordable, community-oriented housing. The site was redeveloped into a 300-bed co-living community with shared workspaces, gyms, and curated events.",
    before: ["Crumbling residential structures", "Unsafe living conditions", "12 families in 3 acres", "No community amenities"],
    afterTitle: "After 16 Months",
    afterMetrics: [
      { label: "Bed Capacity", value: "300" },
      { label: "Occupancy Rate", value: "96%" },
      { label: "Community Events", value: "12 / Month" },
      { label: "Resident Satisfaction", value: "4.6 / 5" },
    ],
    testimonial: "Young professionals used to commute 90 minutes for affordable rent. Now they walk to work and have a community. The waitlist has 200 names.",
    person: "Divya R., Co-living Founder, Hyderabad",
    image: "https://readdy.ai/api/search-image?query=Modern%20co-living%20residential%20community%20in%20India%2C%20bright%20contemporary%20building%20with%20balconies%20and%20shared%20terrace%20spaces%2C%20young%20professionals%20socializing%20in%20courtyard%20lounge%2C%20green%20landscaping%2C%20warm%20daylight%2C%20editorial%20lifestyle%20photography%2C%20inviting%20community%20atmosphere&width=800&height=600&seq=case-coliving-11&orientation=landscape",
  },
  {
    tag: "Industrial",
    tagColor: "bg-secondary-500/10 text-secondary-700",
    title: "Vacant Plot to Precision Manufacturing Zone",
    location: "Chakan, Pune",
    plotSize: "7 Acres",
    strategy: "Precision Engineering Facility",
    timeline: "20 months",
    desc: "7 acres of vacant land in Pune's Chakan auto cluster had no road frontage and was passed over by manufacturers for years. Our cluster-adjacency and power-load model identified it as the ideal site for a precision-engineering supplier to the surrounding automobile OEMs. A 120,000 sq.ft facility with CNC lines and quality labs was constructed.",
    before: ["Vacant plot with no road frontage", "Ignored by manufacturers", "No power infrastructure", "Surrounded by auto cluster but disconnected"],
    afterTitle: "After 20 Months",
    afterMetrics: [
      { label: "Factory Area", value: "120,000 sq.ft" },
      { label: "CNC Lines", value: "14" },
      { label: "Skilled Workforce", value: "520" },
      { label: "OEM Partners", value: "4" },
    ],
    testimonial: "No one wanted this plot because it lacked road frontage. We built the road, built the factory, and now supply four major automobile brands.",
    person: "Sandeep J., Factory Director, Chakan",
    image: "https://readdy.ai/api/search-image?query=Modern%20precision%20manufacturing%20factory%20in%20India%2C%20large%20industrial%20building%20with%20CNC%20machinery%20visible%20through%20glass%20panels%2C%20organized%20production%20floor%2C%20clean%20and%20well-lit%20interior%2C%20trucks%20at%20loading%20bay%2C%20editorial%20industrial%20photography%2C%20professional%20and%20organized%20aesthetic&width=800&height=600&seq=case-industrial-12&orientation=landscape",
  },
];

export default function CaseStudiesPage() {
  const { getContent } = usePageContent('case-studies');
  const heroBadge = stripHtml(getContent('cases-hero-badge')) || 'Success Stories';
  const heroSub = stripHtml(getContent('cases-hero-sub')) || 'How idle land across India became engines of transformation across 12 proven use-cases.';
  const ctaHeading = stripHtml(getContent('cases-cta-heading')) || 'Your Land Could Be the Next Success Story';
  const heroImage = getContent('cases-hero-image') || 'https://readdy.ai/api/search-image?query=Diverse%20Indian%20land%20development%20projects%20collage%2C%20hospital%20school%20warehouse%20and%20hotel%20buildings%20in%20warm%20golden%20hour%20light%2C%20dramatic%20cinematic%20atmosphere%2C%20teal%20and%20amber%20color%20grading%2C%20transformation%20and%20growth%20theme&width=1800&height=800&seq=case-studies-hero-page&orientation=landscape';

  return (
    <PageLayout>
      <PageHero
        badge={heroBadge}
        title="Real"
        highlight="Transformations"
        subtitle={heroSub}
        image={heroImage}
      />

      {/* Case studies grid */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              12 Proven Use-Cases
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              From Idle Land to Thriving Infrastructure
            </h2>
            <p className="text-foreground-600">
              Each case study is backed by AI feasibility analysis, real location data, and documented transformation outcomes — without speculative financial projections.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {cases.map((c, i) => (
              <ScrollReveal key={c.title} delay={i * 80}>
              <article className="rounded-2xl card-dark overflow-hidden flex flex-col h-full">
                {/* Image */}
                <div className="aspect-[4/3] overflow-hidden shrink-0">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                  />
                </div>
                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  {/* Header */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider ${c.tagColor}`}>
                      {c.tag}
                    </span>
                    <span className="text-xs text-foreground-500 flex items-center gap-1">
                      <i className="ri-map-pin-line" />{c.location}
                    </span>
                  </div>
                  <h2 className="text-lg md:text-xl font-display font-bold text-foreground-950 mb-2 leading-snug">
                    {c.title}
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4 flex-1">
                    {c.desc}
                  </p>

                  {/* Meta row */}
                  <div className="flex flex-wrap gap-3 mb-4 text-xs text-foreground-500">
                    <span className="flex items-center gap-1"><i className="ri-ruler-line" />{c.plotSize}</span>
                    <span className="flex items-center gap-1"><i className="ri-time-line" />{c.timeline}</span>
                    <span className="flex items-center gap-1"><i className="ri-lightbulb-line" />{c.strategy}</span>
                  </div>

                  {/* Before / After */}
                  <div className="flex flex-col gap-4 mb-4">
                    <div className="p-4 rounded-xl bg-background-300/30 border border-background-400/20">
                      <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider block mb-2.5">Before</span>
                      <ul className="space-y-2">
                        {c.before.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-xs text-foreground-500 leading-relaxed">
                            <i className="ri-close-circle-line text-foreground-600 shrink-0 mt-0.5" /> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-4 rounded-xl bg-primary-500/5 border border-primary-800/30">
                      <span className="text-xs font-semibold text-primary-600 uppercase tracking-wider block mb-2.5">{c.afterTitle}</span>
                      <div className="space-y-2">
                        {c.afterMetrics.map((m) => (
                          <div key={m.label} className="text-xs">
                            <span className="text-foreground-950 font-semibold">{m.value}</span>
                            <span className="text-foreground-500 ml-1.5">{m.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Testimonial */}
                  <div className="p-3 rounded-xl bg-background-200/50 border border-background-300/30 mt-auto">
                    <p className="text-xs text-foreground-600 italic leading-relaxed">&ldquo;{c.testimonial}&rdquo;</p>
                    <p className="text-xs text-foreground-500 font-medium mt-1.5">— {c.person}</p>
                  </div>
                </div>
              </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            {ctaHeading}
          </h2>
          <p className="text-foreground-600 mb-8">
            Get your AI Feasibility Report and see exactly how your land can be transformed.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
            Start My Transformation
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}